import { useState } from 'react'
import { Eye, MessageSquare, Pause, Pencil, Play, Radar, Square, Trash2 } from 'lucide-react'
import type { ScanHit, ScanSpec, WatcherDetail, WatcherStatus } from '@shared/watch'
import { useStore } from '../../store'
import { useWatcher } from '../../hooks/useWatcher'
import { watchersApi } from '../../lib/api'
import { cn, relativeTime } from '../../lib/utils'
import { untilText, useNow } from '../tasks/schedule-ui'

const px = (n: number) => (Math.abs(n) >= 1 ? n.toFixed(2) : n.toFixed(4))
const pct = (n: number) => `${n > 0 ? '+' : ''}${n.toFixed(2)}%`
const clock = (t: number) => new Date(t).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })

export function WatchStatus({ status, checking }: { status: WatcherStatus; checking?: boolean }) {
  return (
    <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-[var(--color-inset)] px-2 py-0.5 text-[10.5px] font-medium text-fg-muted">
      <span className={cn('h-1.5 w-1.5 rounded-full', status === 'active' ? (checking ? 'bg-up' : 'bg-fg-muted') : status === 'paused' ? 'bg-flat' : 'bg-[var(--color-fg-faint)]')} />
      {status === 'active' ? (checking === false ? 'Active · outside its hours' : 'Active') : status === 'paused' ? 'Paused' : 'Ended'}
    </span>
  )
}

/** A watcher or scan: what it watches, its rules and their live state, what it did — with its controls. */
export function WatcherCard({ id, embedded = false }: { id: string; embedded?: boolean }) {
  const { watcher: w, error, reload } = useWatcher(id)
  if (error && !w) return <div className="card mt-3 px-4 py-3 text-[12px] text-fg-subtle">{error}</div>
  if (!w) return <div className="card mt-3 h-24 animate-pulse" />
  return w.kind === 'scan' ? <ScanBody w={w} reload={reload} embedded={embedded} /> : <WatchBody w={w} reload={reload} embedded={embedded} />
}

function Controls({ w, reload, extra }: { w: WatcherDetail; reload: () => Promise<void>; extra?: React.ReactNode }) {
  const selectChat = useStore((s) => s.selectChat)
  const prefill = useStore((s) => s.prefillComposer)
  const [busy, setBusy] = useState(false)
  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true)
    try {
      await fn()
      await reload()
    } finally {
      setBusy(false)
    }
  }
  const btn = 'inline-flex items-center gap-1.5 rounded-lg border border-[var(--line-soft)] px-2.5 py-1.5 text-[12px] text-fg-muted hover:text-fg disabled:opacity-50'
  return (
    <div className="flex flex-wrap items-center gap-1.5 border-t border-[var(--line-soft)] px-4 py-2.5">
      {extra}
      {w.status === 'active' && (
        <button disabled={busy} onClick={() => act(() => watchersApi.setStatus(w.id, 'paused'))} className={btn}>
          <Pause size={13} /> Pause
        </button>
      )}
      {w.status !== 'active' && (
        <button disabled={busy} onClick={() => act(() => watchersApi.setStatus(w.id, 'active'))} className={btn}>
          <Play size={13} /> {w.status === 'done' ? 'Restart' : 'Resume'}
        </button>
      )}
      {w.status !== 'done' && (
        <button disabled={busy} onClick={() => act(() => watchersApi.setStatus(w.id, 'done'))} className={btn}>
          <Square size={12} /> End
        </button>
      )}
      {w.chatId && (
        <>
          <button onClick={() => void selectChat(w.chatId!)} className={btn}>
            <MessageSquare size={13} /> Chat
          </button>
          <button
            onClick={async () => {
              await selectChat(w.chatId!)
              prefill(`${w.kind === 'scan' ? 'Taramayı' : 'İzleyiciyi'} düzenle (${w.id}): `)
            }}
            className={btn}
          >
            <Pencil size={13} /> Edit in chat
          </button>
        </>
      )}
      <button
        disabled={busy}
        onClick={() => {
          if (confirm(`Delete “${w.name}”?`)) void act(() => watchersApi.remove(w.id))
        }}
        className="ml-auto inline-flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-[12px] text-fg-subtle hover:text-down disabled:opacity-50"
      >
        <Trash2 size={13} /> Delete
      </button>
    </div>
  )
}

function Events({ w }: { w: WatcherDetail }) {
  const ev = w.events.filter((e) => e.kind !== 'created').slice(0, 5)
  if (!ev.length) return null
  return (
    <div className="border-t border-[var(--line-soft)] px-4 py-2.5">
      <div className="mb-1 text-[10px] uppercase tracking-wide text-fg-faint">Recent</div>
      <div className="space-y-0.5">
        {ev.map((e) => (
          <div key={e.id} className="flex gap-2 text-[11.5px]">
            <span className="num shrink-0 text-fg-faint">{relativeTime(e.createdAt)}</span>
            <span className={cn('min-w-0 truncate', e.kind === 'fire' ? 'text-fg' : e.kind.startsWith('ai') ? 'text-fg-muted' : 'text-fg-subtle')}>{e.message}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

function WatchBody({ w, reload, embedded }: { w: WatcherDetail; reload: () => Promise<void>; embedded: boolean }) {
  const spec = w.spec as { symbol: string }
  return (
    <div className={cn('card overflow-hidden', embedded ? 'mt-3' : '')}>
      <div className="px-4 pt-3.5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <Eye size={15} className="shrink-0 text-fg-muted" />
              <span className="truncate text-[14px] font-semibold text-fg">{w.name}</span>
            </div>
            <div className="num mt-1 text-[12.5px] text-fg-muted">
              {spec.symbol}{' '}
              {w.quote ? (
                <>
                  <span className="text-fg">{px(w.quote.price!)}</span>
                  {w.quote.changePct != null && <span className={cn('ml-1', w.quote.changePct > 0 ? 'text-up' : w.quote.changePct < 0 ? 'text-down' : '')}>{pct(w.quote.changePct)}</span>}
                  <span className="text-fg-faint"> · {w.quote.source === 'eodhd-realtime' ? 'real-time' : '15-min delayed'}{w.quote.at ? ` · ${relativeTime(w.quote.at)}` : ''}</span>
                </>
              ) : (
                <span className="text-fg-faint">waiting for data…</span>
              )}
            </div>
          </div>
          <WatchStatus status={w.status} checking={w.status === 'active' ? w.checkingNow : undefined} />
        </div>
        <div className="mt-1 text-[11.5px] text-fg-subtle">Checks {w.hoursText}.</div>
      </div>
      <div className="mt-2.5 space-y-2 border-t border-[var(--line-soft)] px-4 py-3">
        {w.rules.map((r) => (
          <div key={r.id} className="flex gap-2.5">
            <span
              className={cn(
                'mt-1.5 h-2 w-2 shrink-0 rounded-full border',
                r.done ? 'border-[var(--color-fg-faint)] bg-[var(--color-fg-faint)]' : r.holds ? 'border-up bg-up' : 'border-[var(--line)]',
              )}
              title={r.done ? 'done' : r.holds ? 'holds now' : 'waiting'}
            />
            <div className="min-w-0 flex-1">
              <div className={cn('text-[12.5px] leading-snug', r.done ? 'text-fg-subtle' : 'text-fg')}>{r.text}</div>
              <div className="num mt-0.5 text-[11px] text-fg-subtle">
                {r.now ? `now: ${r.now}` : w.status === 'active' ? (w.checkingNow ? 'reading the data…' : 'outside its hours') : '—'}
                {r.fires ? ` · fired ${r.fires}×${r.lastAt ? `, last ${clock(r.lastAt)}` : ''}` : ''}
                {r.done ? ' · done' : ''}
              </div>
            </div>
          </div>
        ))}
      </div>
      <Events w={w} />
      <Controls w={w} reload={reload} />
    </div>
  )
}

function HitRow({ h }: { h: ScanHit }) {
  return (
    <div className="num flex items-center gap-2 text-[11.5px]">
      <span className="w-14 shrink-0 font-medium text-fg">{h.symbol}</span>
      <span className="min-w-0 flex-1 truncate text-fg-subtle">{h.name ?? ''}</span>
      {h.changePct != null && <span className={cn('shrink-0', h.changePct < 0 ? 'text-down' : 'text-up')}>{pct(h.changePct)}</span>}
      {h.fromHighPct != null && <span className="shrink-0 text-fg-faint">{h.fromHighPct.toFixed(0)}% off high</span>}
      {h.rsi != null && <span className="shrink-0 text-fg-faint">RSI {h.rsi.toFixed(0)}</span>}
      {h.isNew && <span className="shrink-0 rounded bg-[var(--color-inset)] px-1 text-[10px] text-fg-muted">new</span>}
    </div>
  )
}

function ScanBody({ w, reload, embedded }: { w: WatcherDetail; reload: () => Promise<void>; embedded: boolean }) {
  const now = useNow()
  const [running, setRunning] = useState(false)
  const spec = w.spec as ScanSpec
  const hits = w.runtime.lastHits ?? []
  return (
    <div className={cn('card overflow-hidden', embedded ? 'mt-3' : '')}>
      <div className="px-4 pt-3.5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <Radar size={15} className="shrink-0 text-fg-muted" />
              <span className="truncate text-[14px] font-semibold text-fg">{w.name}</span>
            </div>
            <div className="mt-1 text-[12px] leading-snug text-fg-muted">{w.hoursText}</div>
          </div>
          <WatchStatus status={w.status} />
        </div>
        <div className="num mt-1.5 text-[11.5px] text-fg-subtle">
          {w.checkingNow ? 'Running now…' : w.status === 'active' && w.nextRunAt ? `Next run ${untilText(w.nextRunAt, now)}` : ''}
          {w.runtime.lastRunAt ? ` · last ${relativeTime(w.runtime.lastRunAt)}` : ' · not run yet'}
          {` · ${spec.action === 'both' ? 'list + AI analysis' : spec.action === 'askAI' ? 'AI analysis' : 'list'} of ${spec.newOnly ? 'new ' : ''}hits`}
        </div>
      </div>
      {hits.length > 0 && (
        <div className="mt-2.5 space-y-1 border-t border-[var(--line-soft)] px-4 py-3">
          <div className="mb-0.5 text-[10px] uppercase tracking-wide text-fg-faint">Last hits</div>
          {hits.slice(0, 10).map((h) => (
            <HitRow key={h.symbol} h={h} />
          ))}
        </div>
      )}
      <Events w={w} />
      <Controls
        w={w}
        reload={reload}
        extra={
          <button
            disabled={running || w.checkingNow}
            onClick={async () => {
              setRunning(true)
              try {
                await watchersApi.runNow(w.id)
                setTimeout(() => void reload(), 1500)
              } finally {
                setRunning(false)
              }
            }}
            className="inline-flex items-center gap-1.5 rounded-lg border border-[var(--line-soft)] px-2.5 py-1.5 text-[12px] text-fg-muted hover:text-fg disabled:opacity-50"
          >
            <Play size={13} /> Run now
          </button>
        }
      />
    </div>
  )
}
